import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Target, PenTool, Code, Rocket } from 'lucide-react';

const StageIcons = [Target, PenTool, Code, Rocket];

export function EngagementPhilosophy() {
  const { t, currentLanguage } = useLanguage();
  const items = t('engagement.items') || [];

  const content = {
    en: {
      label: 'Process',
      title: 'How We Work',
      subtitle: 'A structured approach that turns ideas into production-ready systems.'
    },
    tr: {
      label: 'Süreç',
      title: 'Nasıl Çalışıyoruz',
      subtitle: 'Fikirleri üretime hazır sistemlere dönüştüren yapılandırılmış bir yaklaşım.'
    },
    de: {
      label: 'Prozess',
      title: 'Wie Wir Arbeiten',
      subtitle: 'Ein strukturierter Ansatz, der Ideen in produktionsreife Systeme verwandelt.'
    }
  };

  const c = content[currentLanguage] || content.en;

  return (
    <section
      id="engagement"
      data-testid="engagement-section"
      className="relative py-20 md:py-28 overflow-hidden bg-white"
    >
      {/* Background decorative line */}
      <div 
        className="absolute top-1/2 left-0 right-0 h-px hidden md:block"
        style={{ background: 'rgba(15, 118, 110, 0.1)' }}
      />

      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8">
        {/* Header - Left aligned */}
        <div className="mb-14">
          <p 
            className="text-xs uppercase tracking-widest mb-4"
            style={{ color: '#0F766E' }}
          >
            {c.label}
          </p>
          <h2 
            className="text-3xl md:text-4xl font-semibold tracking-tight mb-3"
            style={{ color: 'var(--text-on-light-primary)' }}
          >
            {c.title}
          </h2>
          <p 
            className="text-base md:text-lg max-w-xl"
            style={{ color: 'var(--text-on-light-muted)' }}
          >
            {c.subtitle}
          </p>
        </div>

        {/* Timeline-style Process */}
        <div className="relative">
          {/* Connecting line for desktop */}
          <div 
            className="absolute top-8 left-0 right-0 h-px hidden md:block"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(15, 118, 110, 0.25), rgba(15, 118, 110, 0.25), transparent)' }}
          />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6">
            {Array.isArray(items) && items.slice(0, 4).map((item, index) => {
              const IconComponent = StageIcons[index] || Target;
              const isLast = index === 3;
              
              return (
                <div 
                  key={index} 
                  className="relative text-center md:text-left"
                >
                  {/* Step indicator - Circle on the line */}
                  <div className="flex flex-col md:flex-row items-center md:items-start gap-4">
                    {/* Number badge with icon */}
                    <div className="relative">
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center relative z-10"
                        style={{ 
                          background: '#FFFFFF',
                          border: '2px solid rgba(15, 118, 110, 0.25)'
                        }}
                      >
                        <div 
                          className="w-12 h-12 rounded-full flex items-center justify-center"
                          style={{ 
                            background: 'linear-gradient(135deg, rgba(15, 118, 110, 0.15) 0%, rgba(15, 118, 110, 0.05) 100%)'
                          }}
                        >
                          <IconComponent 
                            className="w-5 h-5" 
                            style={{ color: '#0F766E' }}
                            strokeWidth={1.5}
                          />
                        </div>
                      </div>
                      {/* Step number */}
                      <span 
                        className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold z-20"
                        style={{ 
                          background: '#0F766E',
                          color: '#FFFFFF',
                          fontFamily: 'JetBrains Mono, monospace',
                          border: '3px solid #FFFFFF',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                        }}
                      >
                        {index + 1}
                      </span>
                    </div>

                    {/* Label */}
                    <div className="md:pt-4">
                      <p 
                        className="text-sm font-medium"
                        style={{ color: 'var(--text-on-light-primary)' }}
                      >
                        {item}
                      </p>
                    </div>
                  </div>

                  {/* Arrow for mobile */}
                  {index < 3 && index % 2 === 0 && (
                    <div 
                      className="md:hidden absolute top-6 -right-3 w-6 h-px"
                      style={{ background: 'rgba(15, 118, 110, 0.25)' }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
